import { useMemo } from "react"
import { DpcRecord } from "interface/dpc/types"

import { numberFormat, round } from 'shared/util'

import {
  Table, TableBody, TableCell, TableContainer, TableContainerProps, TableHead, TableRow,
} from "@mui/material"
import { SummaryFieldTypes } from "features/dpcResult/types"

type DpcColumn = {
  title: string,
  stack: SummaryFieldTypes,
  val: (record: DpcRecord) => number | null | undefined,
  isTotal?: boolean
}
const columns: {
  [columnName: string]: DpcColumn
} = {
  // 疾患別手術別集計
  surgery_s01_case: { stack: 'surgery', val: record => record.surgery?.s01?.cases, title: '手術(1)' },
  surgery_s02_case: { stack: 'surgery', val: record => record.surgery?.s02?.cases, title: '手術(2)' },
  surgery_s03_case: { stack: 'surgery', val: record => record.surgery?.s03?.cases, title: '手術(3)' },
  surgery_s04_case: { stack: 'surgery', val: record => record.surgery?.s04?.cases, title: '手術(4)' },
  surgery_s05_case: { stack: 'surgery', val: record => record.surgery?.s05?.cases, title: '手術(5)' },
  surgery_s06_case: { stack: 'surgery', val: record => record.surgery?.s06?.cases, title: '手術(6)' },
  surgery_s97_case: { stack: 'surgery', val: record => record.surgery?.s97?.cases, title: 'その他手術' },
  surgery_s99_case: { stack: 'surgery', val: record => record.surgery?.s99?.cases, title: '手術なし' },
  surgery_total_case: { stack: 'surgery', val: record => record.surgery?.totalCases, title: '合計', isTotal: true },

  // 疾患別手術有無別処置1有無別集計
  treatment1_00_case: { stack: 'treatment1', val: record => record.treatment1?.s00?.cases, title: '手術なし／処置なし' },
  treatment1_01_case: { stack: 'treatment1', val: record => record.treatment1?.s01?.cases, title: '手術なし／処置あり' },
  treatment1_10_case: { stack: 'treatment1', val: record => record.treatment1?.s10?.cases, title: '手術あり／処置なし' },
  treatment1_11_case: { stack: 'treatment1', val: record => record.treatment1?.s11?.cases, title: '手術あり／処置あり' },
  treatment1_total_case: { stack: 'treatment1', val: record => record.treatment1?.totalCases, title: '合計', isTotal: true },

  // 疾患別手術有無別処置2有無別集計
  treatment2_00_case: { stack: 'treatment2', val: record => record.treatment2?.s00?.cases, title: '手術なし／処置なし' },
  treatment2_01_case: { stack: 'treatment2', val: record => record.treatment2?.s01?.cases, title: '手術なし／処置あり' },
  treatment2_10_case: { stack: 'treatment2', val: record => record.treatment2?.s10?.cases, title: '手術あり／処置なし' },
  treatment2_11_case: { stack: 'treatment2', val: record => record.treatment2?.s11?.cases, title: '手術あり／処置あり' },
  treatment2_total_case: { stack: 'treatment2', val: record => record.treatment2?.totalCases, title: '合計', isTotal: true }
}

type DpcTableProps = {
  list: DpcRecord[]
} & TableContainerProps
export const DpcTable = ({
  list,
  ...others
}: DpcTableProps) => {
  const validColumnNames = useMemo(
    () => Object.keys(columns).filter(
      columnName => {
        const column = columns[columnName]
        return list.some(record => column.val(record))
      }
    ),
    [list]
  )
  const validStacks = useMemo(
    () => {
      const validStacks: {
        stack: string
        count: number
      }[] = []
      validColumnNames.forEach(columnName => {
        const column = columns[columnName]
        const stack = validStacks.find(stack => stack.stack === column.stack)
        if (stack) {
          stack.count++
        } else {
          validStacks.push({ stack: column.stack, count: 1 })
        }
      })
      return validStacks
    },
    [validColumnNames]
  )
  if (validColumnNames.length === 0) return <></>
  return (
    <TableContainer {...others}>
      <Table aria-label="cancer result table" size="small" >
        <TableHead>
          {validStacks.length > 1 ? (
            <>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    width: 0,
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    background: 'white',
                    left: 0,
                    zIndex: 10,
                    borderRight: 1,
                    borderColor: 'divider'
                  }}
                  rowSpan={2}
                >
                  年度
                </TableCell>
                {validStacks.map(({ stack, count }) => (
                  <TableCell
                    key={`stack_${stack}`}
                    align="center"
                    sx={{
                      width: 0,
                      whiteSpace: 'nowrap',
                      borderRight: 1,
                      borderColor: 'divider'
                    }}
                    colSpan={count}
                  >
                    {(
                      stack === 'surgery' ? '手術別' :
                      stack === 'treatment1' ? '処置(1)別' :
                      stack === 'treatment2' ? '処置(2)別' : ''
                    )}
                  </TableCell>
                ))}
                <TableCell/>
              </TableRow>

              <TableRow>
                {validColumnNames.map(columnName => {
                  const column = columns[columnName]
                  return (
                    <TableCell
                      key={`column_${columnName}`}
                      align="center"
                      sx={{
                        width: 0,
                        whiteSpace: 'nowrap',
                        borderRight: column.isTotal === true ? 1 : undefined,
                        borderColor: 'divider'
                      }}
                    >
                      {column.title}
                    </TableCell>
                  )
                })}
                <TableCell/>
              </TableRow>
            </>
          ) : (
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  width: 0,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  background: 'white',
                  left: 0,
                  zIndex: 10,
                  borderRight: 1,
                  borderColor: 'divider'
                }}
              >
                年度
              </TableCell>

              {validColumnNames.map(columnName => {
                const column = columns[columnName]
                return (
                  <TableCell
                    key={`column_${columnName}`}
                    align="center"
                    sx={{ width: 0, whiteSpace: 'nowrap' }}
                  >
                    {column.title}
                  </TableCell>
                )
              })}
              <TableCell/>
            </TableRow>
          )}
        </TableHead>

        <TableBody>
          {list.map((record) => (
            <TableRow key={`year-${record.year}`}>
              <TableCell
                sx={{
                  whiteSpace: "nowrap",
                  position: 'sticky',
                  background: 'white',
                  left: 0,
                  zIndex: 9,
                  borderRight: 1,
                  borderColor: 'divider'
                }}
                align="right"
              >
                {record.year} 年
              </TableCell>

              {validColumnNames.map(columnName => {
                const column = columns[columnName]
                const value = column.val(record)
                return value ? (
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      borderRight: column.isTotal === true ? 1 : undefined,
                      borderColor: 'divider'
                    }}
                    align="right"
                  >
                    {numberFormat(round(value, 1))} 件
                  </TableCell>
                ) : (
                  <TableCell
                    align="center"
                    sx={{
                      background: 'GhostWhite',
                      borderRight: column.isTotal === true ? 1 : undefined,
                      borderColor: 'divider'
                    }}
                  />
                )
              }) }
            </TableRow>
          )) }
        </TableBody>
      </Table>
    </TableContainer>
  )
}